import { PhysicalHazard, StateOfMatter, UnitType } from "encamp-prisma/generated/browser";
import { findSharedMeasurementStorageLocationIds } from "encamp-shared/src/chemicals/measurementSeries";
import { isFiledAnnualTierIIReport } from "encamp-shared/src/tierii/filedReports";
import {
  CombineFacilityYear,
  combineRequiresDensity as sharedCombineRequiresDensity,
  CombineTargetProfile as SharedCombineTargetProfile,
  combineStorageLocationGroupKey,
} from "encamp-shared/src/units/combineDensityRequirement";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { storageTypeFields } from "encamp-shared/src/utils/storageTypeFields";
import {
  DensityUnits,
  InventoryOverlapFieldResolutionInput,
  InventoryOverlapFieldResolutionScope,
  InventoryOverlapResolutionInput,
  InventoryOverlapResolutionStrategy,
  InventoryOverlapStorageLocationAttribute,
  Pressure,
  Temperature,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";

const PRESSURE_VALUES = new Set<string>(Object.values(Pressure));
const TEMPERATURE_VALUES = new Set<string>(Object.values(Temperature));

function isPressureValue(value: string | null): value is Pressure {
  return value !== null && PRESSURE_VALUES.has(value);
}

function isTemperatureValue(value: string | null): value is Temperature {
  return value !== null && TEMPERATURE_VALUES.has(value);
}

type InventoryOverlapCandidateFacility = {
  name: string;
  state?: string | null;
  tierIIReports?: { reportingYear: number; encampStatus: string; reportKind: string }[] | null;
};

export type InventoryOverlapCandidate = {
  id: string;
  name: string;
  associatedFacilities?: { id: string }[] | null;
  facilityChemicalStateFields?:
    | {
        facilityId: string;
        reportingYear: number;
        jurisdiction: string;
        key: string;
        label?: string | null;
        value?: unknown;
      }[]
    | null;
  facilityChemicals?:
    | {
        facilityId: string;
        reportingYear: number;
        maxAmount?: number | null;
        maxAmountLargestContainer?: number | null;
        unit?: string | null;
        facility?: InventoryOverlapCandidateFacility | null;
        storageLocations?:
          | {
              storageLocationId: string;
              maxAmount?: number | null;
              averageAmount?: number | null;
              maxAmountLargestContainer?: number | null;
              unit?: string | null;
              pressure?: string | null;
              otherPressureValue?: string | null;
              temperature?: string | null;
              storageType?: string | null;
              storageTypes?: string[] | null;
              storageTypeDescription?: string | null;
              storageLocation?: { description?: string | null } | null;
            }[]
          | null;
      }[]
    | null;
  // Measurement-series summaries grouped by (facility, reporting year) — the
  // other way a facility can carry this chemical's inventory. A chemical can
  // appear in both this and facilityChemicals for the same facility-year
  // (mid-migration between collection modes), so overlap detection unions
  // the two rather than treating them as mutually exclusive.
  measurementFacilityYears?:
    | {
        facilityId: string;
        reportingYear: number;
        readingCount: number;
        firstReadingAt: string;
        lastReadingAt: string;
        peaks: { amount: number; unit: string }[];
        storageLocations: { id: string; description?: string | null }[];
        facility: InventoryOverlapCandidateFacility;
      }[]
    | null;
};

export type InventoryOverlapStorageLocationDetail = {
  storageLocationId: string;
  name: string;
  maxAmount: number | null;
  averageAmount: number | null;
  maxAmountLargestContainer: number | null;
  unit: string | null;
  pressure: string | null;
  otherPressureValue: string | null;
  temperature: string | null;
  storageTypes: string[];
  storageTypeDescription: string | null;
};

export type InventoryOverlapStateFieldDetail = {
  jurisdiction: string;
  key: string;
  label: string | null;
  value: unknown;
};

export type InventoryOverlapMeasurementPeak = {
  amount: number;
  unit: string;
};

/**
 * A chemical's measurement-series summary for one facility-year, carried on
 * an InventoryOverlapEntry alongside (or instead of) the FC-derived fields.
 * storageLocationNames/Ids are parallel arrays (same order, same length) so
 * a collision check can attribute a shared id back to a display name.
 */
export type InventoryOverlapMeasurementSummary = {
  readingCount: number;
  firstReadingAt: string;
  lastReadingAt: string;
  peaks: InventoryOverlapMeasurementPeak[];
  storageLocationNames: string[];
  storageLocationIds: string[];
};

export type InventoryOverlapEntry = {
  chemicalId: string;
  chemicalName: string;
  maxAmount: number | null;
  maxAmountLargestContainer: number | null;
  unit: string | null;
  storageLocationNames: string[];
  storageLocations: InventoryOverlapStorageLocationDetail[];
  stateFields: InventoryOverlapStateFieldDetail[];
  // Present for a facility-year this chemical carries as a measurement
  // series rather than (or in addition to) a FacilityChemical row. Absent
  // (not null) for FC-only entries, mirroring how the other FC fields go
  // null rather than present-but-empty for measurement-only entries.
  measurementSummary?: InventoryOverlapMeasurementSummary;
};

/**
 * The merged record's state-of-matter and physical-hazard classification —
 * the two ChemicalInput fields resolveCombineTargetUnit needs to pick a
 * jurisdiction's target unit for a facility. Density is threaded separately
 * (as a `mergedHasDensity` boolean) everywhere this type appears, since it
 * gates resolution rather than the target-unit computation.
 */
export type CombineTargetProfile = {
  stateOfMatter: readonly string[] | null | undefined;
  physicalHazards: readonly string[] | null | undefined;
};

export type InventoryOverlap = {
  facilityId: string;
  facilityName: string;
  // The facility's state, for resolveCombineTargetUnit — null when the
  // facility record itself has no state set.
  facilityState: string | null;
  reportingYear: number;
  entries: InventoryOverlapEntry[];
  hasIdenticalAmounts: boolean;
  // The chemical-level `unit`s agree across every entry. When false, the
  // mix can still be combined if it's density-solvable (see
  // combineRequiresDensity) — mass<->mass always converts; mass<->volume
  // needs a density.
  hasSharedChemicalUnit: boolean;
  // A shared storage-location identity group (see combineStorageLocationGroupKey)
  // reports its amount-bearing rows in more than one unit. Combine can still
  // resolve this (see combineRequiresDensity, which folds it in as just
  // another conversion site) — this flag now only gates the merged-amount
  // preview, which can't safely show a summed total across a split it hasn't
  // converted.
  hasStorageLocationUnitConflict: boolean;
  // True when two or more chemicals' measurement series for this
  // facility-year reference the same storage location — mirrors the
  // backend's MEASUREMENT_STORAGE_LOCATION_COLLISION rule. Unlike the FC-side
  // unit conflicts, no density (or anything else client-side) resolves this:
  // a Combine here always fails server-side, so it gates COMBINE as a hard
  // block (see isInventoryOverlapResolved) rather than just a preview
  // limitation.
  hasMeasurementStorageLocationCollision: boolean;
  isFiled: boolean;
};

export function inventoryOverlapKey(
  inventoryOverlap: Pick<InventoryOverlap, "facilityId" | "reportingYear">
): string {
  return `${inventoryOverlap.facilityId}|${inventoryOverlap.reportingYear}`;
}

export function formatEntryAmount(entry: {
  maxAmount: number | null;
  unit: string | null;
}): string {
  if (entry.maxAmount === null) return "—";

  const amountLabel = entry.maxAmount.toLocaleString();
  if (!entry.unit) return amountLabel;

  return `${amountLabel} ${prettyPrintEnumValue(entry.unit).toLowerCase()}`;
}

function computeHasIdenticalAmounts(entries: InventoryOverlapEntry[]): boolean {
  if (entries.length === 0) return false;
  if (entries.some((entry) => entry.maxAmount === null)) return false;

  const [first, ...rest] = entries;
  return rest.every((entry) => entry.maxAmount === first.maxAmount && entry.unit === first.unit);
}

/**
 * True when every entry's chemical-level unit agrees. False doesn't rule out
 * COMBINE — a mass<->mass mismatch converts freely, and a mass<->volume
 * mismatch just needs a density (see combineRequiresDensity).
 */
function computeHasSharedChemicalUnit(entries: InventoryOverlapEntry[]): boolean {
  return new Set(entries.map((entry) => entry.unit ?? null)).size <= 1;
}

/**
 * True when a shared storage-location identity group (location, storage-type
 * set, description) reports its amount-bearing rows in more than one unit —
 * mirroring the backend's combineAmount, which still throws for this split.
 * Unlike a chemical-level unit mismatch, no density fixes this: the split is
 * within a single combined row, not between the two chemicals' totals. A
 * chemical-level match can still hide a per-location split (e.g. both
 * entries say pounds overall but share a location where one stores gallons).
 */
function computeHasStorageLocationUnitConflict(entries: InventoryOverlapEntry[]): boolean {
  const unitsByGroup = new Map<string, { rowCount: number; units: Set<string> }>();
  for (const entry of entries) {
    for (const row of entry.storageLocations) {
      const groupKey = combineStorageLocationGroupKey(row);
      let group = unitsByGroup.get(groupKey);
      if (!group) {
        group = { rowCount: 0, units: new Set() };
        unitsByGroup.set(groupKey, group);
      }
      group.rowCount += 1;
      if ((row.maxAmount != null || row.averageAmount != null) && row.unit != null) {
        group.units.add(row.unit);
      }
    }
  }
  return Array.from(unitsByGroup.values()).some(
    (group) => group.rowCount > 1 && group.units.size > 1
  );
}

export function hasMeasurementSummaries(
  inventoryOverlap: Pick<InventoryOverlap, "entries">
): boolean {
  return inventoryOverlap.entries.some((entry) => entry.measurementSummary);
}

/**
 * Distinct storage-location names referenced by more than one chemical's
 * measurement series in this overlap — the same cross-chemical intersection
 * the backend's MEASUREMENT_STORAGE_LOCATION_COLLISION check performs, via
 * the shared findSharedMeasurementStorageLocationIds. Name resolution stays
 * FE-side (the shared fn only knows ids). Used both to compute
 * hasMeasurementStorageLocationCollision and to name the location(s) in the
 * Combine-disabled tooltip.
 */
export function findMeasurementCollisionLocationNames(
  entries: Pick<InventoryOverlapEntry, "chemicalId" | "measurementSummary">[]
): string[] {
  const nameById = new Map<string, string>();
  const storageLocationIdsByChemical = new Map<string, Set<string>>();
  for (const entry of entries) {
    if (!entry.measurementSummary) continue;
    const { storageLocationIds, storageLocationNames } = entry.measurementSummary;
    storageLocationIds.forEach((locationId, index) => {
      nameById.set(locationId, storageLocationNames[index] ?? "Unnamed location");
    });
    storageLocationIdsByChemical.set(entry.chemicalId, new Set(storageLocationIds));
  }
  const collidingLocationIds = findSharedMeasurementStorageLocationIds(
    storageLocationIdsByChemical
  );
  return Array.from(collidingLocationIds)
    .map((locationId) => nameById.get(locationId) ?? "Unnamed location")
    .sort((a, b) => a.localeCompare(b));
}

function formatShortMeasurementDate(iso: string): string {
  const dateTime = DateTime.fromISO(iso, { zone: "utc" });
  return dateTime.isValid ? dateTime.toLocaleString({ month: "short", day: "numeric" }) : "—";
}

export function formatMeasurementReadingsLabel(
  summary: Pick<
    InventoryOverlapMeasurementSummary,
    "readingCount" | "firstReadingAt" | "lastReadingAt"
  >
): string {
  const first = formatShortMeasurementDate(summary.firstReadingAt);
  const last = formatShortMeasurementDate(summary.lastReadingAt);
  // A single-day series reads better as one date than as "Jul 23 – Jul 23".
  const range = first === last ? first : `${first} – ${last}`;
  return `Readings: ${summary.readingCount} (${range})`;
}

export function formatMeasurementPeaksLabel(
  summary: Pick<InventoryOverlapMeasurementSummary, "peaks">
): string {
  if (summary.peaks.length === 0) return "Peak: —";
  return `Peak: ${summary.peaks
    .map((peak) => formatEntryAmount({ maxAmount: peak.amount, unit: peak.unit }))
    .join(", ")}`;
}

export function formatMeasurementLocationsLabel(
  summary: Pick<InventoryOverlapMeasurementSummary, "storageLocationNames">
): string {
  return `Locations: ${summary.storageLocationNames.length > 0 ? summary.storageLocationNames.join(", ") : "—"}`;
}

const UNIT_TYPE_VALUES = new Set<string>(Object.values(UnitType));

function isUnitType(unit: string): unit is UnitType {
  return UNIT_TYPE_VALUES.has(unit);
}

/** Narrows a possibly-null unit string to UnitType, dropping anything else. */
function toUnitType(unit: string | null): UnitType | null {
  return unit != null && isUnitType(unit) ? unit : null;
}

const STATE_OF_MATTER_VALUES = new Set<string>(Object.values(StateOfMatter));

function isStateOfMatterValue(value: string): value is StateOfMatter {
  return STATE_OF_MATTER_VALUES.has(value);
}

const PHYSICAL_HAZARD_VALUES = new Set<string>(Object.values(PhysicalHazard));

function isPhysicalHazardValue(value: string): value is PhysicalHazard {
  return PHYSICAL_HAZARD_VALUES.has(value);
}

/** Maps an overlap + its loose GraphQL-shaped profile to the shared combine module's neutral facility-year shape. */
function toCombineFacilityYear(overlap: InventoryOverlap): CombineFacilityYear {
  return {
    facilityState: overlap.facilityState,
    entries: overlap.entries.map((entry) => ({
      unit: toUnitType(entry.unit),
      maxAmountLargestContainer: entry.maxAmountLargestContainer,
    })),
    storageLocationRows: overlap.entries.flatMap((entry) =>
      entry.storageLocations.map((row) => ({
        chemicalId: entry.chemicalId,
        storageLocationId: row.storageLocationId,
        storageType: row.storageTypes[0] ?? null,
        storageTypes: row.storageTypes,
        storageTypeDescription: row.storageTypeDescription,
        unit: toUnitType(row.unit),
        maxAmount: row.maxAmount,
        averageAmount: row.averageAmount,
        maxAmountLargestContainer: row.maxAmountLargestContainer,
      }))
    ),
  };
}

/**
 * True when a COMBINE needs a density to reach this facility-year's target
 * unit. Delegates the actual site-by-site decision to the shared
 * combineRequiresDensity (the backend's authoritative implementation) after
 * mapping the overlap and its loose GraphQL profile onto that module's
 * neutral shape.
 */
export function combineRequiresDensity(
  overlap: InventoryOverlap,
  mergedProfile: CombineTargetProfile
): boolean {
  const profile: SharedCombineTargetProfile = {
    stateOfMatter: (mergedProfile.stateOfMatter ?? []).filter(isStateOfMatterValue),
    physicalHazards: (mergedProfile.physicalHazards ?? []).filter(isPhysicalHazardValue),
  };
  return sharedCombineRequiresDensity(toCombineFacilityYear(overlap), profile);
}

/**
 * True when the batch "set all not-yet-filed years to Combine" action may
 * include this overlap. Mirrors the per-card default
 * (initialInventoryOverlapResolutions): unit mismatches at either level and
 * identical amounts stay explicit per-overlap choices — a batch click must
 * not silently flip an overlap into a strategy that needs more input
 * (a density) or would double-count duplicate data entry.
 */
export function isBatchCombineEligible(inventoryOverlap: InventoryOverlap): boolean {
  return (
    inventoryOverlap.hasSharedChemicalUnit &&
    !inventoryOverlap.hasStorageLocationUnitConflict &&
    !inventoryOverlap.hasMeasurementStorageLocationCollision &&
    !inventoryOverlap.hasIdenticalAmounts
  );
}

// Thin wrapper over the shared isFiledAnnualTierIIReport (the backend's
// getAffectedFiledYears / measurementFacilityYears apply the same rule).
// Shared by both the FC-derived and measurement-derived paths in
// findInventoryOverlaps.
function isFiledYear(
  tierIIReports:
    | { reportingYear: number; encampStatus: string; reportKind: string }[]
    | null
    | undefined,
  reportingYear: number
): boolean {
  return (tierIIReports ?? []).some(
    (report) => report.reportingYear === reportingYear && isFiledAnnualTierIIReport(report)
  );
}

function buildEntryStateFields(
  candidate: Pick<InventoryOverlapCandidate, "facilityChemicalStateFields">,
  facilityId: string,
  reportingYear: number
): InventoryOverlapStateFieldDetail[] {
  return (candidate.facilityChemicalStateFields ?? [])
    .filter((sf) => sf.facilityId === facilityId && sf.reportingYear === reportingYear)
    .map((sf) => ({
      jurisdiction: sf.jurisdiction,
      key: sf.key,
      label: sf.label ?? null,
      value: sf.value,
    }));
}

type FacilityYearRecord = {
  facilityId: string;
  facilityName: string;
  facilityState: string | null;
  reportingYear: number;
  isFiled: boolean;
  entries: Map<string, InventoryOverlapEntry>;
};

/**
 * Finds (facility, reporting year) pairs where more than one merge candidate
 * carries inventory — as a FacilityChemical row, a measurement series, or
 * both — for the same facility-year. Each such inventory overlap must be
 * resolved (KEEP one entry, COMBINE them, or leave BLANK) before the merge
 * can proceed.
 *
 * Runs in two full passes over the candidates — FC rows first, then
 * measurement summaries — rather than interleaving them per candidate, so
 * the facility name/state fallback (FC wins when both exist for a
 * facility-year) doesn't depend on which candidate happens to be processed
 * first.
 */
export function findInventoryOverlaps(candidates: InventoryOverlapCandidate[]): InventoryOverlap[] {
  const entriesByFacilityYear = new Map<string, FacilityYearRecord>();

  for (const candidate of candidates) {
    for (const facilityChemical of candidate.facilityChemicals ?? []) {
      const key = inventoryOverlapKey(facilityChemical);
      let record = entriesByFacilityYear.get(key);
      if (!record) {
        record = {
          facilityId: facilityChemical.facilityId,
          facilityName: facilityChemical.facility?.name ?? "Unknown facility",
          facilityState: facilityChemical.facility?.state ?? null,
          reportingYear: facilityChemical.reportingYear,
          isFiled: false,
          entries: new Map(),
        };
        entriesByFacilityYear.set(key, record);
      }
      record.isFiled =
        record.isFiled ||
        isFiledYear(facilityChemical.facility?.tierIIReports, facilityChemical.reportingYear);
      const storageLocations = (facilityChemical.storageLocations ?? []).map((sl) => ({
        storageLocationId: sl.storageLocationId,
        name: sl.storageLocation?.description ?? "Unnamed location",
        maxAmount: sl.maxAmount ?? null,
        averageAmount: sl.averageAmount ?? null,
        maxAmountLargestContainer: sl.maxAmountLargestContainer ?? null,
        unit: sl.unit ?? null,
        pressure: sl.pressure ?? null,
        otherPressureValue: sl.otherPressureValue ?? null,
        temperature: sl.temperature ?? null,
        storageTypes: storageTypeFields(sl).storageTypes,
        storageTypeDescription: sl.storageTypeDescription ?? null,
      }));
      record.entries.set(candidate.id, {
        chemicalId: candidate.id,
        chemicalName: candidate.name,
        maxAmount: facilityChemical.maxAmount ?? null,
        maxAmountLargestContainer: facilityChemical.maxAmountLargestContainer ?? null,
        unit: facilityChemical.unit ?? null,
        storageLocationNames: Array.from(
          new Set(
            (facilityChemical.storageLocations ?? [])
              .map((storageLocation) => storageLocation.storageLocation?.description)
              .filter((description): description is string => !!description)
          )
        ),
        storageLocations,
        stateFields: buildEntryStateFields(
          candidate,
          facilityChemical.facilityId,
          facilityChemical.reportingYear
        ),
      });
    }
  }

  for (const candidate of candidates) {
    for (const measurementGroup of candidate.measurementFacilityYears ?? []) {
      const key = inventoryOverlapKey(measurementGroup);
      let record = entriesByFacilityYear.get(key);
      if (!record) {
        // No FC rows claimed this facility-year in the pass above, so this
        // measurement group is the only source for its name/state.
        record = {
          facilityId: measurementGroup.facilityId,
          facilityName: measurementGroup.facility.name ?? "Unknown facility",
          facilityState: measurementGroup.facility.state ?? null,
          reportingYear: measurementGroup.reportingYear,
          isFiled: false,
          entries: new Map(),
        };
        entriesByFacilityYear.set(key, record);
      }
      record.isFiled =
        record.isFiled ||
        isFiledYear(measurementGroup.facility.tierIIReports, measurementGroup.reportingYear);

      const storageLocationIds = measurementGroup.storageLocations.map((sl) => sl.id);
      const storageLocationNames = measurementGroup.storageLocations.map(
        (sl) => sl.description ?? "Unnamed location"
      );
      const measurementSummary: InventoryOverlapMeasurementSummary = {
        readingCount: measurementGroup.readingCount,
        firstReadingAt: measurementGroup.firstReadingAt,
        lastReadingAt: measurementGroup.lastReadingAt,
        peaks: measurementGroup.peaks.map((peak) => ({ amount: peak.amount, unit: peak.unit })),
        storageLocationNames,
        storageLocationIds,
      };

      const existingEntry = record.entries.get(candidate.id);
      if (existingEntry) {
        // The rare mixed-within-one-chemical case: this candidate already
        // has an FC row for this facility-year: layer the measurement
        // summary onto it rather than replacing the FC fields.
        existingEntry.measurementSummary = measurementSummary;
      } else {
        record.entries.set(candidate.id, {
          chemicalId: candidate.id,
          chemicalName: candidate.name,
          maxAmount: null,
          maxAmountLargestContainer: null,
          unit: null,
          storageLocationNames: [],
          storageLocations: [],
          stateFields: buildEntryStateFields(
            candidate,
            measurementGroup.facilityId,
            measurementGroup.reportingYear
          ),
          measurementSummary,
        });
      }
    }
  }

  return Array.from(entriesByFacilityYear.values())
    .filter((record) => record.entries.size > 1)
    .map((record) => {
      const entries = Array.from(record.entries.values());
      return {
        facilityId: record.facilityId,
        facilityName: record.facilityName,
        facilityState: record.facilityState,
        reportingYear: record.reportingYear,
        entries,
        hasIdenticalAmounts: computeHasIdenticalAmounts(entries),
        hasSharedChemicalUnit: computeHasSharedChemicalUnit(entries),
        hasStorageLocationUnitConflict: computeHasStorageLocationUnitConflict(entries),
        hasMeasurementStorageLocationCollision:
          findMeasurementCollisionLocationNames(entries).length > 0,
        isFiled: record.isFiled,
      };
    })
    .sort(
      (a, b) => a.facilityName.localeCompare(b.facilityName) || a.reportingYear - b.reportingYear
    );
}

/**
 * Counts distinct (facility, reporting year) pairs across all candidates —
 * the union of FC-derived and measurement-derived facility-years — whether
 * or not they overlap. Used to report how many facility-years merge
 * automatically (i.e. don't require a decision).
 */
export function countDistinctFacilityYears(candidates: InventoryOverlapCandidate[]): number {
  const keys = new Set<string>();
  for (const candidate of candidates) {
    for (const facilityChemical of candidate.facilityChemicals ?? []) {
      keys.add(inventoryOverlapKey(facilityChemical));
    }
    for (const measurementGroup of candidate.measurementFacilityYears ?? []) {
      keys.add(inventoryOverlapKey(measurementGroup));
    }
  }
  return keys.size;
}

/**
 * Counts distinct facilities associated with a set of merge candidates.
 * Moved here (from the picker) so the count that feeds the impact map and
 * confirmation copy is unit-tested directly.
 */
export function countAffectedFacilities(candidates: InventoryOverlapCandidate[]): number {
  return new Set(
    candidates.flatMap((candidate) => (candidate.associatedFacilities ?? []).map((f) => f.id))
  ).size;
}

export type MergeImpact = {
  // Chemicals being merged (N → 1).
  entryCount: number;
  // Facility-years where the merged candidates overlap and need a decision.
  decisionCount: number;
  // Of those, already-filed vs. still-open.
  filedCount: number;
  openCount: number;
  // Facility-years that merge automatically (no overlap, so no decision).
  autoMergeCount: number;
};

/**
 * Summarizes a merge's blast radius for the impact-map banner: how many
 * catalog entries collapse into one, how many facility-years need an
 * explicit decision (split by filed/open), and how many merge automatically.
 * Moved here (from the picker's autoMergeFacilityYearCount) so the count is
 * unit-tested directly.
 */
export function buildMergeImpact(
  candidates: InventoryOverlapCandidate[],
  overlaps: InventoryOverlap[]
): MergeImpact {
  const filedCount = overlaps.filter((overlap) => overlap.isFiled).length;
  return {
    entryCount: candidates.length,
    decisionCount: overlaps.length,
    filedCount,
    openCount: overlaps.length - filedCount,
    autoMergeCount: countDistinctFacilityYears(candidates) - overlaps.length,
  };
}

export type StorageLocationGroupIdentity = {
  storageLocationId: string;
  storageTypes: string[];
  storageTypeDescription: string | null;
};

export function storageLocationConflictKey(
  group: StorageLocationGroupIdentity,
  attribute: InventoryOverlapStorageLocationAttribute
): string {
  const sortedStorageTypes = [...group.storageTypes].sort();
  return `SL|${group.storageLocationId}|${JSON.stringify(sortedStorageTypes)}|${
    group.storageTypeDescription ?? ""
  }|${attribute}`;
}

export function stateFieldConflictKey(jurisdiction: string, key: string): string {
  return `SF|${jurisdiction}|${key}`;
}

type ConflictOption = { chemicalId: string; chemicalName: string; display: string };

// A distinct value held by one or more rows in a combined storage-location
// group. `resolutionFields` are the typed fields to emit on the mutation
// when this option wins (spread directly — never re-derived from
// `serializedValue`, which exists only to key the UI's stored choice).
export type StorageLocationConflictOption = {
  serializedValue: string;
  display: string;
  chemicalNames: string[];
  resolutionFields: Pick<
    InventoryOverlapFieldResolutionInput,
    "pressure" | "otherPressureValue" | "temperature"
  >;
};

export type StorageLocationCombine = StorageLocationGroupIdentity & {
  storageLocationName: string;
  context: { label: string; display: string }[];
  conflicts: {
    attribute: InventoryOverlapStorageLocationAttribute;
    options: StorageLocationConflictOption[];
  }[];
};

export type StateFieldConflict = {
  jurisdiction: string;
  key: string;
  label: string;
  options: ConflictOption[];
};

export type CombineConflicts = {
  storageLocations: StorageLocationCombine[];
  stateFieldConflicts: StateFieldConflict[];
};

type StorageLocationCluster = {
  attribute: InventoryOverlapStorageLocationAttribute;
  label: string;
  hasValue: (row: InventoryOverlapStorageLocationDetail) => boolean;
  resolutionFields: (
    row: InventoryOverlapStorageLocationDetail
  ) => StorageLocationConflictOption["resolutionFields"];
  display: (row: InventoryOverlapStorageLocationDetail) => string;
};

function displayEnum(value: string | null): string {
  return value ? prettyPrintEnumValue(value) : "—";
}

// Mirrors the backend's attribute clusters in
// apps/api/src/resolvers/chemical/combineInventoryOverlaps.ts — the merge is
// rejected if a conflict detected there has no resolution built here. Amount
// is not a cluster: it always sums (never picked). Storage type is not a
// cluster either: it is part of the group's identity (see
// combineStorageLocationGroupKey), not a pickable conflict.
const STORAGE_LOCATION_CLUSTERS: StorageLocationCluster[] = [
  {
    attribute: InventoryOverlapStorageLocationAttribute.Pressure,
    label: "Pressure",
    hasValue: (row) => row.pressure != null,
    resolutionFields: (row) => ({
      pressure: isPressureValue(row.pressure) ? row.pressure : null,
      otherPressureValue: row.otherPressureValue,
    }),
    display: (row) =>
      row.otherPressureValue
        ? `${displayEnum(row.pressure)} (${row.otherPressureValue})`
        : displayEnum(row.pressure),
  },
  {
    attribute: InventoryOverlapStorageLocationAttribute.Temperature,
    label: "Temperature",
    hasValue: (row) => row.temperature != null,
    resolutionFields: (row) => ({
      temperature: isTemperatureValue(row.temperature) ? row.temperature : null,
    }),
    display: (row) => displayEnum(row.temperature),
  },
];

function displayStateFieldValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value === null || value === undefined) return "—";
  return String(value);
}

/**
 * Detects the field conflicts a COMBINE resolution must settle: attributes
 * that genuinely differ on a storage location both chemicals reference, and
 * (jurisdiction, key) state fields where both chemicals have differing
 * values. Matching or one-sided values are returned as read-only context (or
 * omitted) — they never require a pick.
 */
export function findCombineConflicts(inventoryOverlap: InventoryOverlap): CombineConflicts {
  const rowsByGroup = new Map<
    string,
    StorageLocationGroupIdentity & {
      name: string;
      rows: {
        chemicalId: string;
        chemicalName: string;
        row: InventoryOverlapStorageLocationDetail;
      }[];
    }
  >();
  for (const entry of inventoryOverlap.entries) {
    for (const row of entry.storageLocations) {
      const identity: StorageLocationGroupIdentity = {
        storageLocationId: row.storageLocationId,
        storageTypes: [...row.storageTypes].sort(),
        storageTypeDescription: row.storageTypeDescription,
      };
      const groupKey = combineStorageLocationGroupKey(identity);
      let group = rowsByGroup.get(groupKey);
      if (!group) {
        group = { ...identity, name: row.name, rows: [] };
        rowsByGroup.set(groupKey, group);
      }
      group.rows.push({ chemicalId: entry.chemicalId, chemicalName: entry.chemicalName, row });
    }
  }

  const storageLocations: StorageLocationCombine[] = [];
  for (const group of rowsByGroup.values()) {
    // A lone row (even from a chemical with just one row here) has nothing
    // to combine with. Multi-row groups can conflict even when every row
    // belongs to the same chemical (per-container rows sharing an identity).
    if (group.rows.length <= 1) continue;

    const context: StorageLocationCombine["context"] = [];
    const conflicts: StorageLocationCombine["conflicts"] = [];

    const amountRows = group.rows.filter((r) => r.row.maxAmount != null);
    if (amountRows.length > 0) {
      const unit = amountRows.find((r) => r.row.unit)?.row.unit ?? null;
      const total = amountRows.reduce((sum, r) => sum + (r.row.maxAmount ?? 0), 0);
      context.push({ label: "Amount", display: formatEntryAmount({ maxAmount: total, unit }) });
    }

    for (const cluster of STORAGE_LOCATION_CLUSTERS) {
      const valued = group.rows.filter((r) => cluster.hasValue(r.row));
      if (valued.length === 0) continue;

      const optionsBySerializedValue = new Map<
        string,
        {
          display: string;
          chemicalNames: Set<string>;
          resolutionFields: StorageLocationConflictOption["resolutionFields"];
        }
      >();
      for (const valuedRow of valued) {
        const resolutionFields = cluster.resolutionFields(valuedRow.row);
        const serializedValue = JSON.stringify(resolutionFields);
        let option = optionsBySerializedValue.get(serializedValue);
        if (!option) {
          option = {
            display: cluster.display(valuedRow.row),
            chemicalNames: new Set(),
            resolutionFields,
          };
          optionsBySerializedValue.set(serializedValue, option);
        }
        option.chemicalNames.add(valuedRow.chemicalName);
      }

      if (optionsBySerializedValue.size <= 1) {
        context.push({ label: cluster.label, display: cluster.display(valued[0].row) });
      } else {
        conflicts.push({
          attribute: cluster.attribute,
          options: Array.from(optionsBySerializedValue.entries()).map(
            ([serializedValue, option]) => ({
              serializedValue,
              display: option.display,
              chemicalNames: Array.from(option.chemicalNames),
              resolutionFields: option.resolutionFields,
            })
          ),
        });
      }
    }
    if (conflicts.length > 0) {
      storageLocations.push({
        storageLocationId: group.storageLocationId,
        storageLocationName: group.name,
        storageTypes: group.storageTypes,
        storageTypeDescription: group.storageTypeDescription,
        context,
        conflicts,
      });
    }
  }

  const optionsByField = new Map<
    string,
    {
      jurisdiction: string;
      key: string;
      label: string;
      options: ConflictOption[];
      values: Set<string>;
    }
  >();
  for (const entry of inventoryOverlap.entries) {
    for (const stateField of entry.stateFields) {
      const key = stateFieldConflictKey(stateField.jurisdiction, stateField.key);
      let field = optionsByField.get(key);
      if (!field) {
        field = {
          jurisdiction: stateField.jurisdiction,
          key: stateField.key,
          label: stateField.label ?? stateField.key,
          options: [],
          values: new Set(),
        };
        optionsByField.set(key, field);
      }
      field.options.push({
        chemicalId: entry.chemicalId,
        chemicalName: entry.chemicalName,
        display: displayStateFieldValue(stateField.value),
      });
      field.values.add(JSON.stringify(stateField.value ?? null));
    }
  }

  const stateFieldConflicts = Array.from(optionsByField.values())
    .filter((field) => field.values.size > 1)
    .map(({ jurisdiction, key, label, options }) => ({ jurisdiction, key, label, options }));

  return { storageLocations, stateFieldConflicts };
}

export type InventoryOverlapResolutionState = {
  strategy: InventoryOverlapResolutionStrategy;
  keepChemicalId?: string;
  fieldChoices: Record<string, string>;
};

/**
 * Initial per-overlap strategy: not-yet-filed years default to COMBINE (the
 * batch can be flipped in the UI). Identical-amount overlaps default to KEEP
 * first entry regardless — identical entries are almost always duplicate data
 * entry, and combining them would double-count. Filed years and
 * unit-mismatched overlaps also default to KEEP.
 */
export function initialInventoryOverlapResolutions(
  inventoryOverlaps: InventoryOverlap[]
): Record<string, InventoryOverlapResolutionState> {
  const resolutions: Record<string, InventoryOverlapResolutionState> = {};
  for (const inventoryOverlap of inventoryOverlaps) {
    const key = inventoryOverlapKey(inventoryOverlap);
    if (
      !inventoryOverlap.isFiled &&
      !inventoryOverlap.hasIdenticalAmounts &&
      inventoryOverlap.hasSharedChemicalUnit &&
      !inventoryOverlap.hasStorageLocationUnitConflict &&
      !inventoryOverlap.hasMeasurementStorageLocationCollision
    ) {
      resolutions[key] = {
        strategy: InventoryOverlapResolutionStrategy.Combine,
        fieldChoices: {},
      };
    } else {
      resolutions[key] = {
        strategy: InventoryOverlapResolutionStrategy.Keep,
        keepChemicalId: inventoryOverlap.hasIdenticalAmounts
          ? inventoryOverlap.entries[0]?.chemicalId
          : undefined,
        fieldChoices: {},
      };
    }
  }
  return resolutions;
}

/**
 * Rebuilds resolution state for a (possibly changed) set of overlaps while
 * preserving prior choices that still apply — so selections survive a round
 * trip back to the review step. The candidate set can change there, so a
 * prior choice only carries over while it remains valid: a kept chemical
 * must still be in the overlap, a COMBINE needs its shared unit, and field
 * choices must still match a current conflict option. Anything else falls
 * back to the defaults.
 */
export function reconcileInventoryOverlapResolutions(
  inventoryOverlaps: InventoryOverlap[],
  previous: Record<string, InventoryOverlapResolutionState>
): Record<string, InventoryOverlapResolutionState> {
  const resolutions = initialInventoryOverlapResolutions(inventoryOverlaps);
  for (const inventoryOverlap of inventoryOverlaps) {
    const key = inventoryOverlapKey(inventoryOverlap);
    const prior = previous[key];
    if (!prior) continue;

    if (prior.strategy === InventoryOverlapResolutionStrategy.Blank) {
      resolutions[key] = { strategy: prior.strategy, fieldChoices: {} };
      continue;
    }

    if (prior.strategy === InventoryOverlapResolutionStrategy.Keep) {
      const keepStillPresent = inventoryOverlap.entries.some(
        (entry) => entry.chemicalId === prior.keepChemicalId
      );
      resolutions[key] = {
        strategy: prior.strategy,
        keepChemicalId: keepStillPresent ? prior.keepChemicalId : undefined,
        fieldChoices: {},
      };
      continue;
    }

    // A unit mismatch (chemical-level or storage-location-level) just needs a
    // density, entered separately, so a prior COMBINE choice survives it —
    // only stale field choices below get dropped.
    const conflicts = findCombineConflicts(inventoryOverlap);
    const validChoices = new Map<string, Set<string>>();
    for (const location of conflicts.storageLocations) {
      for (const conflict of location.conflicts) {
        validChoices.set(
          storageLocationConflictKey(location, conflict.attribute),
          new Set(conflict.options.map((option) => option.serializedValue))
        );
      }
    }
    for (const conflict of conflicts.stateFieldConflicts) {
      validChoices.set(
        stateFieldConflictKey(conflict.jurisdiction, conflict.key),
        new Set(conflict.options.map((option) => option.chemicalId))
      );
    }
    resolutions[key] = {
      strategy: prior.strategy,
      fieldChoices: Object.fromEntries(
        Object.entries(prior.fieldChoices).filter(([conflictKey, choice]) =>
          validChoices.get(conflictKey)?.has(choice)
        )
      ),
    };
  }
  return resolutions;
}

/**
 * Reseeds the Step-2 combine-density override when re-entering the overlap
 * step. A density chosen in Step 1 (on the merged record input) wins —
 * it is the operator's newest explicit choice — but when Step 1 still has
 * no density, a value already typed inline in Step 2 must survive the
 * round trip rather than being silently discarded.
 */
export function reconcileCombineDensityOverride(
  previous: { density: number | null; densityUnits: DensityUnits | null },
  input: { density?: number | null; densityUnits?: DensityUnits | null }
): { density: number | null; densityUnits: DensityUnits | null } {
  if (input.density != null && input.densityUnits != null) {
    return { density: input.density, densityUnits: input.densityUnits };
  }
  return previous;
}

export function isInventoryOverlapResolved(
  inventoryOverlap: InventoryOverlap,
  state: InventoryOverlapResolutionState | undefined,
  mergedHasDensity: boolean,
  mergedProfile: CombineTargetProfile
): boolean {
  if (!state) return false;
  if (state.strategy === InventoryOverlapResolutionStrategy.Blank) {
    return true;
  }
  if (state.strategy === InventoryOverlapResolutionStrategy.Keep) {
    return !!state.keepChemicalId;
  }
  // Unlike an SL unit split (density-solvable, see below), two chemicals'
  // measurement series sharing a storage location is genuinely unsolvable
  // client-side — the backend rejects it outright (Leave blank is the only
  // way out), so no density or field choice ever resolves this.
  if (inventoryOverlap.hasMeasurementStorageLocationCollision) return false;
  // A shared storage-location unit split is folded into combineRequiresDensity
  // (as just another SL group) rather than treated as separately unsolvable —
  // a mass<->mass split there converts freely, same as at the chemical level.
  if (combineRequiresDensity(inventoryOverlap, mergedProfile) && !mergedHasDensity) return false;

  const conflicts = findCombineConflicts(inventoryOverlap);
  return (
    conflicts.storageLocations.every((location) =>
      location.conflicts.every(
        (conflict) => !!state.fieldChoices[storageLocationConflictKey(location, conflict.attribute)]
      )
    ) &&
    conflicts.stateFieldConflicts.every(
      (conflict) => !!state.fieldChoices[stateFieldConflictKey(conflict.jurisdiction, conflict.key)]
    )
  );
}

export function combinedMaxAmountPreview(inventoryOverlap: InventoryOverlap): string {
  const unit = inventoryOverlap.entries.find((entry) => entry.unit)?.unit ?? null;
  const parts = inventoryOverlap.entries.map((entry) =>
    formatEntryAmount({ maxAmount: entry.maxAmount ?? 0, unit: entry.unit })
  );
  const total = inventoryOverlap.entries.reduce((sum, entry) => sum + (entry.maxAmount ?? 0), 0);
  return `${parts.join(" + ")} → ${formatEntryAmount({ maxAmount: total, unit })}`;
}

/**
 * Maps the UI resolution state to the mutation contract. Callers must gate on
 * isInventoryOverlapResolved for every overlap first; an unresolved overlap
 * here is a programming error.
 */
export function buildInventoryOverlapResolutionInputs(
  inventoryOverlaps: InventoryOverlap[],
  resolutions: Record<string, InventoryOverlapResolutionState>,
  mergedHasDensity: boolean,
  mergedProfile: CombineTargetProfile
): InventoryOverlapResolutionInput[] {
  return inventoryOverlaps.map((inventoryOverlap) => {
    const key = inventoryOverlapKey(inventoryOverlap);
    const state = resolutions[key];
    if (
      !state ||
      !isInventoryOverlapResolved(inventoryOverlap, state, mergedHasDensity, mergedProfile)
    ) {
      throw new Error(`Inventory overlap ${key} is not fully resolved.`);
    }

    if (state.strategy === InventoryOverlapResolutionStrategy.Blank) {
      return {
        facilityId: inventoryOverlap.facilityId,
        reportingYear: inventoryOverlap.reportingYear,
        strategy: InventoryOverlapResolutionStrategy.Blank,
      };
    }

    if (state.strategy === InventoryOverlapResolutionStrategy.Combine) {
      const conflicts = findCombineConflicts(inventoryOverlap);
      const fieldResolutions: InventoryOverlapFieldResolutionInput[] = [
        ...conflicts.storageLocations.flatMap((location) =>
          location.conflicts.map((conflict) => {
            const chosenSerializedValue =
              state.fieldChoices[storageLocationConflictKey(location, conflict.attribute)];
            const chosenOption = conflict.options.find(
              (option) => option.serializedValue === chosenSerializedValue
            );
            if (!chosenOption) {
              throw new Error(`Inventory overlap ${key} is not fully resolved.`);
            }
            return {
              scope: InventoryOverlapFieldResolutionScope.StorageLocation,
              storageLocationId: location.storageLocationId,
              storageTypes: location.storageTypes,
              storageTypeDescription: location.storageTypeDescription,
              attribute: conflict.attribute,
              ...chosenOption.resolutionFields,
            };
          })
        ),
        ...conflicts.stateFieldConflicts.map((conflict) => ({
          scope: InventoryOverlapFieldResolutionScope.StateField,
          jurisdiction: conflict.jurisdiction,
          key: conflict.key,
          chosenFromChemicalId:
            state.fieldChoices[stateFieldConflictKey(conflict.jurisdiction, conflict.key)],
        })),
      ];
      return {
        facilityId: inventoryOverlap.facilityId,
        reportingYear: inventoryOverlap.reportingYear,
        strategy: InventoryOverlapResolutionStrategy.Combine,
        fieldResolutions,
      };
    }

    return {
      facilityId: inventoryOverlap.facilityId,
      reportingYear: inventoryOverlap.reportingYear,
      strategy: InventoryOverlapResolutionStrategy.Keep,
      keepChemicalId: state.keepChemicalId,
    };
  });
}
